import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";

import { EmptyState } from "@/components/empty-state";
import { SectionHeading } from "@/components/section-heading";
import { WebhookEventReplay } from "@/components/webhook-event-replay";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { formatMoney } from "@/lib/money";
import {
  getFinancePermissions,
  getTipDigestStatus,
  listStuckTipsForReplay,
  replayStuckTipEvents,
  sendTipDigestNow,
} from "@/lib/payments/admin-tips.functions";

export const Route = createFileRoute("/_authenticated/admin/tip-replay")({
  head: () => ({
    meta: [
      { title: "Stuck tips & replay — Quibblies admin" },
      {
        name: "description",
        content:
          "Find tips left pending because a payment webhook never arrived, replay the provider's verdict, and release parked quiet-hours digests.",
      },
      { property: "og:title", content: "Stuck tips & replay — Quibblies admin" },
      {
        property: "og:description",
        content: "Re-read stranded payments from the provider and settle them.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: TipReplayPage,
});

function TipReplayPage() {
  const queryClient = useQueryClient();
  const loadStuck = useServerFn(listStuckTipsForReplay);
  const loadPermissions = useServerFn(getFinancePermissions);
  const loadDigest = useServerFn(getTipDigestStatus);
  const replay = useServerFn(replayStuckTipEvents);
  const flush = useServerFn(sendTipDigestNow);

  const [minutes, setMinutes] = React.useState(30);
  const [selected, setSelected] = React.useState<string[]>([]);

  const permissions = useQuery({
    queryKey: ["finance-permissions"],
    queryFn: () => loadPermissions(),
    retry: false,
  });

  const digest = useQuery({
    queryKey: ["tip-digest-status"],
    queryFn: () => loadDigest(),
    retry: false,
  });

  const stuck = useQuery({
    queryKey: ["stuck-tips", minutes],
    queryFn: () => loadStuck({ data: { olderThanMinutes: minutes } }),
    retry: false,
  });

  const replayMutation = useMutation({
    mutationFn: (tipIds: string[]) => replay({ data: { tipIds } }),
    onSuccess: (results) => {
      const applied = results.filter((row) => row.applied).length;
      toast.success(
        applied > 0
          ? `${applied} tip${applied === 1 ? "" : "s"} settled from the provider's record`
          : "Nothing changed — the provider has no new verdict for those tips",
        { description: results.map((row) => row.message)[0] },
      );
      setSelected([]);
      void queryClient.invalidateQueries({ queryKey: ["stuck-tips"] });
    },
    onError: (err: Error) => toast.error("Replay failed", { description: err.message }),
  });

  const flushMutation = useMutation({
    mutationFn: () => flush(),
    onSuccess: (result) => {
      toast.success(
        result.sent > 0
          ? `Sent ${result.sent} digest${result.sent === 1 ? "" : "s"} covering ${result.entries} update${result.entries === 1 ? "" : "s"}`
          : "There was nothing parked to send",
      );
      void queryClient.invalidateQueries({ queryKey: ["tip-digest-status"] });
    },
    onError: (err: Error) => toast.error("Could not send digests", { description: err.message }),
  });

  const rows = stuck.data ?? [];
  const canResolve = permissions.data?.canResolveTips === true;

  if (permissions.error) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6">
        <SectionHeading
          title="Stuck tips"
          description="This area is restricted to administrators."
        />
        <p className="mt-4 text-sm text-destructive">{permissions.error.message}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6">
      <SectionHeading
        title="Stuck tips & replay"
        description="Payments that finished at the provider but never reached us leave a tip stranded as pending. Replaying re-reads the provider's own record and applies whatever it says — it can never invent an outcome or apply one twice."
      />

      {!canResolve && permissions.data ? (
        <p className="mt-6 rounded-xl border border-border/60 bg-surface/50 p-4 text-sm text-muted-foreground">
          {permissions.data.reason ??
            "You can review this page, but only administrators with financial access can replay payments."}
        </p>
      ) : null}

      <section className="mt-8 rounded-2xl border border-border/60 bg-surface/50 p-5">
        <h2 className="text-sm font-medium text-foreground">Quiet-hours digest</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {digest.data
            ? `${digest.data.pending} tip update${digest.data.pending === 1 ? "" : "s"} parked${
                digest.data.quietNow
                  ? ", and quiet hours are running now. They go out as one email per member as soon as the window closes."
                  : ". These will go out on the next digest run."
              }`
            : "Checking what's parked…"}
        </p>
        <Button
          className="mt-3"
          size="sm"
          variant="soft"
          onClick={() => flushMutation.mutate()}
          disabled={flushMutation.isPending || (digest.data?.pending ?? 0) === 0}
        >
          {flushMutation.isPending ? "Sending…" : "Send parked digests now"}
        </Button>
      </section>

      <div className="mt-8 flex flex-wrap items-end gap-3">
        <label className="text-sm">
          <span className="block text-muted-foreground">Pending for at least (minutes)</span>
          <Input
            type="number"
            className="mt-1 w-32"
            min={1}
            max={10080}
            value={String(minutes)}
            onChange={(event) => setMinutes(Math.max(1, Number(event.target.value) || 1))}
          />
        </label>
        <Button
          size="sm"
          disabled={!canResolve || selected.length === 0 || replayMutation.isPending}
          onClick={() => replayMutation.mutate(selected)}
        >
          {replayMutation.isPending
            ? "Replaying…"
            : `Replay ${selected.length || ""} selected`.trim()}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setSelected(rows.map((row) => row.id))}
          disabled={rows.length === 0}
        >
          Select all
        </Button>
      </div>

      {stuck.isLoading ? (
        <Skeleton className="mt-6 h-56 w-full rounded-2xl" />
      ) : stuck.error ? (
        <p className="mt-6 text-sm text-destructive">{stuck.error.message}</p>
      ) : rows.length === 0 ? (
        <div className="mt-6">
          <EmptyState
            title="Nothing is stuck"
            description="Every recent tip has a settled outcome from the payment provider."
          />
        </div>
      ) : (
        <ul className="mt-6 space-y-3">
          {rows.map((row) => (
            <li
              key={row.id}
              className="flex items-start gap-3 rounded-2xl border border-border/60 bg-surface/50 p-5"
            >
              <Checkbox
                className="mt-1"
                checked={selected.includes(row.id)}
                onCheckedChange={(checked) =>
                  setSelected((current) =>
                    checked === true
                      ? [...current, row.id]
                      : current.filter((id) => id !== row.id),
                  )
                }
                aria-label={`Select tip ${row.id}`}
              />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground">
                  {formatMoney(row.amountMinor, row.currency)} · pending {row.minutesPending} min
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {row.events === 0
                    ? "No provider event has ever been recorded for this tip."
                    : `${row.events} provider event${row.events === 1 ? "" : "s"} recorded, last outcome “${row.lastResult}”.`}
                </p>
                <p className="mt-1 break-all font-mono text-xs text-muted-foreground">
                  {row.reference ?? "no reference"} · {row.id}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}

      {replayMutation.data && replayMutation.data.length > 0 ? (
        <div className="mt-8 rounded-2xl border border-border/60 bg-surface/50 p-5">
          <h2 className="text-sm font-medium text-foreground">Last replay</h2>
          <ul className="mt-2 space-y-2 text-sm text-muted-foreground">
            {replayMutation.data.map((row) => (
              <li key={row.tipId}>
                <span className="font-mono text-xs">{row.tipId.slice(0, 8)}</span> — {row.from}
                {row.to ? ` → ${row.to}` : ""}: {row.message}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="mt-10">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Replay a captured webhook event
        </h2>
        <WebhookEventReplay />
      </div>
    </div>

  );
}
